/**
 * Image processing utilities for asset uploads
 * Handles validation, resizing, and compression
 */

// Configuration constants
export const IMAGE_CONFIG = {
  // Maximum dimensions for different asset categories
  maxDimensions: {
    thumbnails: 512,    // Thumbnails: max 512x512
    icons: 256,         // Icons: max 256x256
    references: 2048,   // Reference images: max 2048x2048
    general: 1024,      // General assets: max 1024x1024
  },
  // Maximum file size before processing (reject if larger than this)
  maxOriginalFileSize: 20 * 1024 * 1024, // 20MB - reject files larger than this
  // Maximum file size after processing
  maxProcessedFileSize: 5 * 1024 * 1024, // 5MB
  // JPEG quality for compression (0-1)
  jpegQuality: 0.85,
  // PNG compression (only affects encoding, not lossy)
  pngQuality: 0.9,
  // Allowed MIME types
  allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
};

export type AssetCategory = 'thumbnails' | 'icons' | 'references' | 'general';

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
  originalSize: number;
  originalDimensions?: { width: number; height: number };
}

export interface ProcessedImage {
  dataUrl: string;
  width: number;
  height: number;
  size: number;
  mimeType: string;
}

/**
 * Validate an image file before processing
 */
export const validateImageFile = async (file: File): Promise<ImageValidationResult> => {
  // Check MIME type
  if (!IMAGE_CONFIG.allowedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.type}. Allowed types: JPEG, PNG, GIF, WebP, SVG`,
      originalSize: file.size,
    };
  }

  // Check file size
  if (file.size > IMAGE_CONFIG.maxOriginalFileSize) {
    const maxMB = IMAGE_CONFIG.maxOriginalFileSize / (1024 * 1024);
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large: ${fileMB}MB. Maximum allowed: ${maxMB}MB`,
      originalSize: file.size,
    };
  }

  // For SVGs, skip dimension check (they're scalable)
  if (file.type === 'image/svg+xml') {
    return { valid: true, originalSize: file.size };
  }

  // Get image dimensions
  try {
    const dimensions = await getImageDimensions(file);
    return {
      valid: true,
      originalSize: file.size,
      originalDimensions: dimensions,
    };
  } catch (err) {
    return {
      valid: false,
      error: 'Failed to read image dimensions. File may be corrupted.',
      originalSize: file.size,
    };
  }
};

/**
 * Get image dimensions from a file
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

/**
 * Resize and compress an image
 */
export const processImage = async (
  file: File,
  category: AssetCategory = 'general'
): Promise<ProcessedImage> => {
  // SVGs don't need processing
  if (file.type === 'image/svg+xml') {
    const dataUrl = await fileToDataUrl(file);
    return {
      dataUrl,
      width: 0,
      height: 0,
      size: file.size,
      mimeType: file.type,
    };
  }

  const maxDimension = IMAGE_CONFIG.maxDimensions[category];
  const img = await loadImage(file);
  
  // Calculate new dimensions maintaining aspect ratio
  let { width, height } = img;
  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height / width) * maxDimension);
      width = maxDimension;
    } else {
      width = Math.round((width / height) * maxDimension);
      height = maxDimension;
    }
  }

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  
  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Determine output format and quality
  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const quality = outputType === 'image/jpeg' 
    ? IMAGE_CONFIG.jpegQuality 
    : IMAGE_CONFIG.pngQuality;

  const dataUrl = canvas.toDataURL(outputType, quality);
  const size = Math.round((dataUrl.length - `data:${outputType};base64,`.length) * 0.75);

  return { dataUrl, width, height, size, mimeType: outputType };
};

/**
 * Load an image from a file
 */
const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

/**
 * Convert a file to a data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Process and validate an image file for upload
 * Returns processed image or throws an error with user-friendly message
 */
export const prepareImageForUpload = async (
  file: File,
  category: AssetCategory = 'general'
): Promise<ProcessedImage> => {
  // Validate first
  const validation = await validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Process the image
  const processed = await processImage(file, category);

  // Check final size
  if (processed.size > IMAGE_CONFIG.maxProcessedFileSize) {
    const maxMB = IMAGE_CONFIG.maxProcessedFileSize / (1024 * 1024);
    const finalMB = (processed.size / (1024 * 1024)).toFixed(1);
    throw new Error(
      `Image still too large after processing: ${finalMB}MB. ` +
      `Maximum: ${maxMB}MB. Try a smaller image.`
    );
  }

  return processed;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

